import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { cn } from "~/utils";

interface StarRatingProps {
  rating?: number;
  maxRating?: number;
  size?: "sm" | "md" | "lg";
  interactive?: boolean;
  onChange?: (rating: number) => void;
  className?: string;
}

export function StarRating({
  rating = 0,
  maxRating = 5,
  size = "md",
  interactive = false,
  onChange,
  className,
}: StarRatingProps) {
  const [hoverRating, setHoverRating] = useState(0);
  const [currentRating, setCurrentRating] = useState(rating);

  useEffect(() => {
    setCurrentRating(rating);
  }, [rating]);

  const handleClick = (index: number) => {
    if (!interactive) return;

    const newRating = index + 1;
    setCurrentRating(newRating);
    onChange?.(newRating);
  };

  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  const displayRating = interactive ? hoverRating || currentRating : rating;

  return (
    <div
      className={cn("flex items-center gap-1", className)}
      onMouseLeave={() => interactive && setHoverRating(0)}
    >
      {Array.from({ length: maxRating }).map((_, index) => (
        <button
          key={index}
          type={interactive ? "button" : "button"}
          className={cn(
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm",
            interactive ? "cursor-pointer" : "cursor-default"
          )}
          onClick={() => handleClick(index)}
          onMouseEnter={() => interactive && setHoverRating(index + 1)}
          disabled={!interactive}
          aria-label={`${index + 1} star${index !== 0 ? "s" : ""}`}
        >
          <Star
            className={cn(
              sizeClasses[size],
              "transition-all",
              index < displayRating
                ? "fill-[#FFDF00] text-[#FF9529]"
                : "fill-muted text-muted-foreground"
            )}
          />
        </button>
      ))}
    </div>
  );
}
